import React from 'react';
import { Authenticated } from '@refinedev/core';
import { ErrorComponent, ThemedLayoutV2, RefineThemes } from '@refinedev/antd';
import { RefineKbar, RefineKbarProvider } from '@refinedev/kbar';
import { Route, Routes, Outlet, Navigate } from 'react-router-dom';
import { ConfigProvider, Layout } from 'antd';

import { DashboardPage } from './pages/dashboard';
import { SandboxList } from './pages/sandboxes/list';
import { SandboxShow } from './pages/sandboxes/show';
import { SandboxCreate } from './pages/sandboxes/create';
import { LoginPage } from './pages/login';
import { AnalyticsPage } from './pages/analytics';

import { useRealTimeUpdates } from './hooks/useRealTimeUpdates';
import { ConnectionStatus } from './components/layout/ConnectionStatus';
import { ErrorBoundary } from './components/ErrorBoundary';

import '@refinedev/antd/dist/reset.css';

const { Header } = Layout;

/**
 * Authenticated layout wrapper with real-time updates and connection status
 */
const AuthenticatedLayout: React.FC = () => {
  const { isConnected, isConnecting } = useRealTimeUpdates();

  return (
    <ThemedLayoutV2
      Header={() => (
        <Header style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          padding: '0 24px',
          background: '#fff',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <ConnectionStatus isConnected={isConnected} isConnecting={isConnecting} />
        </Header>
      )}
    >
      <Outlet />
    </ThemedLayoutV2>
  );
};

export const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <RefineKbarProvider>
        <ConfigProvider theme={RefineThemes.Blue}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              element={
                <Authenticated
                  key="authenticated-routes"
                  fallback={<Navigate to="/login" replace />}
                >
                  <AuthenticatedLayout />
                </Authenticated>
              }
            >
              <Route index element={<ErrorBoundary><DashboardPage /></ErrorBoundary>} />
              <Route path="/sandboxes">
                <Route index element={<ErrorBoundary><SandboxList /></ErrorBoundary>} />
                <Route path="show/:id" element={<ErrorBoundary><SandboxShow /></ErrorBoundary>} />
                <Route path="create" element={<ErrorBoundary><SandboxCreate /></ErrorBoundary>} />
              </Route>
              <Route path="/analytics" element={<ErrorBoundary><AnalyticsPage /></ErrorBoundary>} />
              <Route path="*" element={<ErrorComponent />} />
            </Route>
          </Routes>
          <RefineKbar />
        </ConfigProvider>
      </RefineKbarProvider>
    </ErrorBoundary>
  );
};
